/**
 * Resume Bank page - Search and browse all resumes
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { resumeBankAPI, ResumeBank } from '../api/client';
import TopRibbon from '../components/TopRibbon';

const ResumeBankPage: React.FC = () => {
  const navigate = useNavigate();

  const [searchQuery, setSearchQuery] = useState('');
  const [resumes, setResumes] = useState<ResumeBank[]>([]);
  const [searching, setSearching] = useState(false);
  const [loading, setLoading] = useState(true);

  // Load all resumes on mount
  React.useEffect(() => {
    const loadAllResumes = async () => {
      setLoading(true);
      try {
        const response = await resumeBankAPI.search('', { limit: 2000 });
        setResumes(response.data);
      } catch (error) {
        console.error('Failed to load resumes:', error);
      } finally {
        setLoading(false);
      }
    };
    loadAllResumes();
  }, []);

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      // If search is empty, reload all resumes
      setSearching(true);
      try {
        const response = await resumeBankAPI.search('', { limit: 2000 });
        setResumes(response.data);
      } catch (error) {
        console.error('Failed to load resumes:', error);
      } finally {
        setSearching(false);
      }
      return;
    }

    setSearching(true);

    try {
      const response = await resumeBankAPI.search(searchQuery, { limit: 2000 });
      setResumes(response.data);
    } catch (error) {
      console.error('Search failed:', error);
    } finally {
      setSearching(false);
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Unknown';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <>
      <TopRibbon />
      <div style={{ minHeight: '100vh', background: '#f8f9fa', paddingTop: '64px' }}>
      {/* Header */}
      <div style={{
        background: 'white',
        borderBottom: '1px solid #e5e7eb',
        padding: '24px'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <button onClick={() => navigate('/roles')} style={{ marginBottom: '16px' }}>
            ← Back to Roles
          </button>
          <h1 style={{ margin: '0 0 8px 0', fontSize: '28px', fontWeight: 700 }}>
            Resume Bank
          </h1>
          <p style={{ margin: 0, color: '#6b7280' }}>
            Search through all uploaded resumes using keywords, skills, or experience
          </p>
        </div>
      </div>

      {/* Search section */}
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '40px 20px' }}>
        <div style={{
          background: 'white',
          borderRadius: '12px',
          padding: '32px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
          marginBottom: '32px'
        }}>
          <div style={{ display: 'flex', gap: '12px' }}>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              placeholder="Search by skills, keywords, experience... (e.g., 'Python FastAPI', 'senior engineer')"
              style={{
                flex: 1,
                padding: '14px 16px',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                fontSize: '16px'
              }}
              disabled={searching}
            />
            <button
              onClick={handleSearch}
              disabled={searching}
              style={{
                padding: '14px 32px',
                background: '#2d5016',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: 600,
                cursor: searching ? 'not-allowed' : 'pointer',
                opacity: searching ? 0.6 : 1
              }}
            >
              {searching ? 'Searching...' : searchQuery.trim() ? 'Search' : 'Show All'}
            </button>
          </div>

          <div style={{
            marginTop: '16px',
            padding: '12px',
            background: '#f0fdf4',
            borderLeft: '3px solid #2d5016',
            borderRadius: '4px',
            fontSize: '14px',
            color: '#374151'
          }}>
            <strong>💡 Full-text search:</strong> The search looks through extracted resume text
            using PostgreSQL's powerful full-text search capabilities.
          </div>
        </div>

        {/* Results */}
        {loading || searching ? (
          <div style={{ textAlign: 'center', padding: '80px 20px' }}>
            <div style={{
              width: '48px',
              height: '48px',
              border: '4px solid #e5e7eb',
              borderTopColor: '#2d5016',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto 16px'
            }} />
            <p>{loading ? 'Loading resume bank...' : 'Searching...'}</p>
          </div>
        ) : resumes.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '80px 20px',
            background: 'white',
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)'
          }}>
            <div style={{ fontSize: '64px', marginBottom: '16px' }}>📋</div>
            <h2 style={{ margin: '0 0 12px 0' }}>
              {searchQuery ? 'No results found' : 'No resumes yet'}
            </h2>
            <p style={{ color: '#6b7280' }}>
              {searchQuery
                ? 'Try a different search query or upload more resumes.'
                : 'Upload resume binders to roles to populate your resume bank.'}
            </p>
          </div>
        ) : resumes.length > 0 ? (
          <div>
            <div style={{
              marginBottom: '16px',
              fontSize: '14px',
              color: '#6b7280',
              fontWeight: 600
            }}>
              Found {resumes.length} resumes
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
              gap: '20px'
            }}>
              {resumes.map((resume) => (
                <div
                  key={resume.id}
                  style={{
                    background: 'white',
                    borderRadius: '12px',
                    padding: '20px',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                    transition: 'all 0.2s',
                    cursor: 'pointer'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.boxShadow = '0 4px 16px rgba(0, 0, 0, 0.12)';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.08)';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  <div style={{ marginBottom: '12px' }}>
                    <h3 style={{
                      margin: '0 0 4px 0',
                      fontSize: '16px',
                      fontWeight: 700,
                      color: '#1a1a1a'
                    }}>
                      {resume.extracted_name || resume.original_filename || 'Unknown Resume'}
                    </h3>
                    <div style={{ fontSize: '13px', color: '#6b7280' }}>
                      {resume.page_count} page{resume.page_count !== 1 ? 's' : ''} •{' '}
                      {resume.file_size ? `${(resume.file_size / 1024).toFixed(0)} KB` : 'Unknown size'}
                    </div>
                  </div>

                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    paddingTop: '12px',
                    borderTop: '1px solid #f3f4f6'
                  }}>
                    <div style={{ fontSize: '12px', color: '#9ca3af' }}>
                      Uploaded {formatDate(resume.created_at)}
                    </div>
                    <div style={{ fontSize: '12px', fontWeight: 600, color: '#2d5016' }}>
                      {resume.evaluation_count || 0} evaluation{resume.evaluation_count !== 1 ? 's' : ''}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : null}
      </div>

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
    </>
  );
};

export default ResumeBankPage;
